// Added by Sejune Oh on 2020-02-25
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useEffect, useState, useMemo } from 'react'
import { createStyles, makeStyles } from '@material-ui/core'

// Helper
import Intl from '@/helper/Intl'

// UI
import { RadsScorePercentageView } from '@/components/worklist/RadsScorePercentageView'
import { HistogramBarChart, HistogramBarPartProps } from '@/components/worklist/HistogramBarChart'

// Interface
import { LCSStatisticStyleTypes } from '@/components/worklist/WebComponents/LCSStatisticWrapper'
export interface StatisticsHistogramProps {
  score1: number
  score2: number
  score3: number
  score4: number
  positiveRate: number
  suspiciousRate: number
  totalCount: number
  readingCount: number
  totalLabel?: string
  readingLabel?: string
  unreadingLabel?: string
  guideLine?: string
  /** WC용 Styles  */
  classes?: LCSStatisticStyleTypes
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const COLOR_BLUE = '#00a7e1'
export const COLOR_YELLOW = '#ffcb51'
export const COLOR_RED = '#ec2c2c'

export const COLOR_RADS3 = '#f2c40f'
export const COLOR_RADS4 = '#ec2c2c'

const StatisticsHistogram = ({
  score1,
  score2,
  score3,
  score4,
  positiveRate,
  suspiciousRate,
  readingCount,
  totalCount,
  guideLine,
  totalLabel: totalLabelParam,
  readingLabel: readingLabelParam,
  unreadingLabel: unreadingLabelParam,
  classes: webComponentClassses,
  ...rest
}: StatisticsHistogramProps) => {
  const classes = webComponentClassses
    ? webComponentClassses['statisticshistogram']
    : makeStyles(() => createStyles({ ...customStyles }))()

  const totalLabel = totalLabelParam || Intl.getIntlText({ id: 'htmljs_react.worklist.statisticshistogram.label_total' })
  const readingLabel = readingLabelParam || Intl.getIntlText({ id: 'htmljs_react.worklist.statisticshistogram.label_read' })
  const unreadingLabel = unreadingLabelParam || Intl.getIntlText({ id: 'htmljs_react.worklist.statisticshistogram.label_unread' })
  const guideLineLabel = useMemo(() => {
    if (!guideLine) return ''
    if (guideLine === 'EUPS') return Intl.getIntlText({ id: `htmljs_react.worklist.statisticshistogram.label_guideline_nelson_plus_rads` })
    return Intl.getIntlText({ id: `htmljs_react.worklist.statisticshistogram.label_guideline_lung_rads` })
  }, [guideLine])

  const [data, setData] = useState<HistogramBarPartProps[]>([])
  const [tooltipText, setTooltipText] = useState('')

  // eslint-disable-next-line
  useEffect(() => setHistogramBarChartInfo(), [])
  // eslint-disable-next-line
  useEffect(() => setHistogramBarChartInfo(), [score1, score2, score3, score4, totalCount])

  const setHistogramBarChartInfo = () => {
    setTooltipText(
      `RADS Score 1 : ${score1}\nRADS Score 2 : ${score2}\nRADS Score 3 : ${score3}\nRADS Score 4 : ${score4}`
    )

    setData([
      { label: 'Suspicious', count: score4, color: COLOR_RED },
      { label: 'Positive', count: score3, color: COLOR_YELLOW },
      { label: 'Read', count: score1 + score2, color: COLOR_BLUE },
    ])
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.firstCol}>
        <div className={classes.countWrapper}>
          <span>
            <small>{totalLabel}</small> {totalCount}
          </span>
          <span>
            <small>{readingLabel}</small> {readingCount}
          </span>
          <span>
            <small>{unreadingLabel}</small> {totalCount - readingCount}
          </span>
        </div>
        <div className={classes.histogramWrapper}>
          <HistogramBarChart
            total={totalCount}
            data={data}
            tooltipText={tooltipText}
            direction={'rtl'}
            barHeight={6}
            classes={webComponentClassses}
          ></HistogramBarChart>
        </div>
      </div>
      <div className={classes.secondCol}>
        <div className={classes.radsScoreLabel}>{guideLineLabel}</div>
        <div className={classes.radsScoreWrapper}>
          <RadsScorePercentageView
            score={'3+'}
            rate={positiveRate}
            panelColor={COLOR_RADS3}
            classes={webComponentClassses}
          ></RadsScorePercentageView>
        </div>
        <div className={classes.radsScoreWrapper}>
          <RadsScorePercentageView
            score={'4'}
            rate={suspiciousRate}
            panelColor={COLOR_RADS4}
            classes={webComponentClassses}
          ></RadsScorePercentageView>
        </div>
      </div>
    </div>
  )
}

export const customStyles: Record<string, any> = {
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#1a1a1a',
  },
  firstCol: {
    minWidth: '194px',
    width: 'fit-content',
    padding: '9px 10px 9px 8px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    '&:after': {
      content: '""',
      position: 'absolute',
      right: '0',
      top: '9px',
      bottom: '9px',
      width: '1px',
      backgroundColor: '#000',
    },
  },
  countWrapper: {
    flex: '1',
    lineHeight: '1',
    textAlign: 'center',
    minWidth: 'max-content',
    '&>span': {
      position: 'relative',
      marginLeft: '10px',
      fontSize: '12px',
      color: '#fff',
      fontWeight: '900',
      '&:before': {
        content: '""',
        position: 'absolute',
        borderLeft: '1px solid #fff',
        left: '-6px',
        top: '4px',
        bottom: '4px',
        width: '1px',
      },
      '&:first-of-type': {
        marginLeft: 0,
        '&:before': {
          content: 'unset',
        },
      },
      '&>small': {
        fontWeight: 'normal',
        fontSize: '10px',
        marginRight: '2px',
        color: '#a5a5a5',
      },
    },
  },
  histogramWrapper: {},
  secondCol: {
    flexBasis: '82px',
    color: '#fff',
  },
  radsScoreLabel: {
    fontSize: '8px',
    lineHeight: '8px',
    textAlign: 'center',
    padding: '1.5px 0 3px 0',
  },
  radsScoreWrapper: {
    paddingLeft: '8px',
    marginRight: '10px',
    '&:first-of-type': {
      borderBottom: '1px solid #000',
    },
  },
}

export default StatisticsHistogram
